package com.speedsoftware.nibbler.ui.boards

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.BoardColumnDto
import com.speedsoftware.nibbler.models.IssueType
import com.speedsoftware.nibbler.models.TaskDto
import com.speedsoftware.nibbler.models.TaskPriority
import com.speedsoftware.nibbler.ui.components.AssigneeAvatarStack
import com.speedsoftware.nibbler.ui.components.IssueTypeBadge
import com.speedsoftware.nibbler.ui.components.PriorityIcon
import com.speedsoftware.nibbler.ui.components.PropertyIcon
import com.speedsoftware.nibbler.ui.components.PropertyIconView
import com.speedsoftware.nibbler.ui.components.VersionTag
import com.speedsoftware.nibbler.ui.components.formatShortDate
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/** One status column: a rounded panel with a coloured dot, name, count badge, "+" and a menu. */
@Composable
fun KanbanColumn(
    column: BoardColumnDto,
    tasks: List<TaskDto>,
    isHovered: Boolean,
    dragDropState: KanbanDragDropState,
    onTaskClick: (TaskDto) -> Unit,
    onDrop: (TaskDto, String, String?) -> Unit,
    canMoveLeft: Boolean = false,
    canMoveRight: Boolean = false,
    canDelete: Boolean = false,
    onAddIssue: () -> Unit = {},
    onEditColumn: () -> Unit = {},
    onMoveColumnLeft: () -> Unit = {},
    onMoveColumnRight: () -> Unit = {},
    onDeleteColumn: () -> Unit = {}
) {
    val accent = parseColumnColor(column.color)
    // The drag state needs to know which cards this column shows, top to bottom, to work out the drop slot
    SideEffect { dragDropState.registerColumnTasks(column.id, tasks.map { it.id }) }
    val showDropMarker = isHovered && dragDropState.draggingTask != null && dragDropState.dropWouldChange(column.id)
    val markerBefore = dragDropState.dropBeforeTaskId

    Column(
        modifier = Modifier
            .width(316.dp)
            .fillMaxHeight()
            .background(if (isHovered) NibblerColors.AccentSoft.copy(alpha = 0.5f) else NibblerColors.SurfaceContainerLowest, RoundedCornerShape(14.dp))
            .border(if (isHovered) 2.dp else 1.dp, if (isHovered) NibblerColors.Accent else NibblerColors.OutlineVariant, RoundedCornerShape(14.dp))
            .kanbanDropTarget(column.id, dragDropState)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(start = 16.dp, end = 6.dp, top = 12.dp, bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Box(modifier = Modifier.size(10.dp).background(accent, CircleShape))
                Text(
                    column.name,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = NibblerColors.OnSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f, fill = false)
                )
                Text(
                    "${tasks.size}",
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                    color = NibblerColors.OnSurfaceVariant,
                    modifier = Modifier.background(NibblerColors.SurfaceContainerHighest, RoundedCornerShape(10.dp)).padding(horizontal = 8.dp, vertical = 2.dp)
                )
            }
            Box(modifier = Modifier.size(30.dp).clickable { onAddIssue() }, contentAlignment = Alignment.Center) {
                PropertyIconView(PropertyIcon.PLUS, size = 16.dp)
            }
            ColumnMenuButton(
                canMoveLeft = canMoveLeft,
                canMoveRight = canMoveRight,
                canDelete = canDelete,
                onEdit = onEditColumn,
                onMoveLeft = onMoveColumnLeft,
                onMoveRight = onMoveColumnRight,
                onDelete = onDeleteColumn
            )
        }
        Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.OutlineVariant))

        LazyColumn(
            verticalArrangement = Arrangement.spacedBy(10.dp),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(12.dp),
            modifier = Modifier.fillMaxSize()
        ) {
            tasks.forEach { task ->
                if (showDropMarker && markerBefore == task.id) item(key = "drop-marker") { DropPlaceholder() }
                item(key = task.id) {
                    KanbanCard(
                        task = task,
                        onClick = { onTaskClick(task) },
                        dragDropState = dragDropState,
                        onDrop = onDrop,
                        modifier = Modifier.kanbanCardBounds(task.id, dragDropState)
                    )
                }
            }
            if (showDropMarker && (markerBefore == null || tasks.none { it.id == markerBefore })) {
                item(key = "drop-marker") { DropPlaceholder() }
            }
        }
    }
}

/** Dashed target shown at the end of the column a card is being dragged over. */
@Composable
private fun DropPlaceholder() {
    val dash = NibblerColors.Accent
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(64.dp)
            .drawBehind {
                drawRoundRect(
                    color = dash,
                    cornerRadius = androidx.compose.ui.geometry.CornerRadius(12.dp.toPx()),
                    style = Stroke(width = 1.5.dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(10f, 8f)))
                )
            },
        contentAlignment = Alignment.Center
    ) {
        Text("Drop here", fontSize = 13.sp, fontWeight = FontWeight.Medium, color = NibblerColors.Accent)
    }
}

/**
 * An issue as a card: key and priority icon, title, a short description, type / version chips and a footer
 * with the date, comment count and the people on it.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun KanbanCard(
    task: TaskDto,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    dragDropState: KanbanDragDropState? = null,
    onDrop: ((TaskDto, String, String?) -> Unit)? = null,
    isGhost: Boolean = false
) {
    val shape = RoundedCornerShape(12.dp)
    Column(
        modifier = modifier
            .fillMaxWidth()
            .then(if (!isGhost && dragDropState != null && onDrop != null) Modifier.kanbanCardDrag(task, dragDropState, onDrop) else Modifier)
            .alpha(if (!isGhost && dragDropState?.draggingTask?.id == task.id) 0.35f else 1f)
            .clip(shape)
            .clickable { onClick() }
            .background(NibblerColors.SurfaceContainer, shape)
            .border(if (isGhost) 2.dp else 1.dp, if (isGhost) NibblerColors.Accent else NibblerColors.OutlineVariant, shape)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Text(task.key, fontSize = 12.sp, fontWeight = FontWeight.Medium, color = NibblerColors.OnSurfaceVariant)
            PriorityIcon(task.priority, size = 16.dp)
        }

        Text(
            task.title,
            fontSize = 15.sp,
            fontWeight = FontWeight.Medium,
            color = NibblerColors.OnSurface,
            maxLines = 3,
            overflow = TextOverflow.Ellipsis
        )

        if (task.description.isNotBlank()) {
            Text(task.description, fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant, maxLines = 2, overflow = TextOverflow.Ellipsis)
        }

        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            IssueTypeBadge(task.type)
            task.version?.takeIf { it.isNotBlank() }?.let { VersionTag(it) }
        }

        Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.OutlineVariant))

        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(14.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                PropertyIconView(PropertyIcon.CALENDAR, size = 14.dp)
                Text(formatShortDate(task.createdAt), fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
            }
            if (task.commentCount > 0) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                    PropertyIconView(PropertyIcon.COMMENT, size = 14.dp)
                    Text("${task.commentCount}", fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
                }
            }
            Box(modifier = Modifier.weight(1f))
            AssigneeAvatarStack(task.assignees, size = 24.dp)
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun KanbanCardPreview() {
    Column(modifier = Modifier.background(NibblerColors.Background).padding(16.dp).width(316.dp)) {
        KanbanCard(
            task = TaskDto(
                id = "t1", key = "NIB-42", boardId = "b", projectId = "p", title = "Refactor compose architecture into decoupled modular components",
                description = "Deconstruct screens into reusable components with previews.", type = IssueType.FEATURE, status = "c1",
                priority = TaskPriority.HIGH, version = "1.0.0", commentCount = 3, createdAt = "2026-10-03T10:00:00", updatedAt = ""
            ),
            onClick = {}
        )
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun KanbanColumnPreview() {
    Box(modifier = Modifier.background(NibblerColors.Background).padding(16.dp).height(400.dp)) {
        KanbanColumn(
            column = BoardColumnDto(id = "c1", boardId = "b", name = "In progress", color = "#F5B544"),
            tasks = emptyList(),
            isHovered = false,
            dragDropState = KanbanDragDropState(),
            onTaskClick = {},
            onDrop = { _, _, _ -> }
        )
    }
}
